// ══════════════════════════════════════════════════════════════════
//  ED CRM - Notificação nativa do navegador
// ══════════════════════════════════════════════════════════════════
//  Camada mais barata de "notificação de verdade": a API `Notification`
//  do próprio navegador, SEM service worker. Vale só com uma aba do CRM
//  aberta (mesmo que em segundo plano) — com o navegador fechado, quem
//  avisa é o dispatcher do Config/104 pelos canais externos.
//
//  Tudo aqui é defensivo: navegador sem suporte, permissão negada ou
//  construtor que estoura nunca podem derrubar a UI.
// ══════════════════════════════════════════════════════════════════

export type BrowserNotifyPermission = 'unsupported' | 'default' | 'granted' | 'denied';

export const browserNotifySupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

export const browserNotifyPermission = (): BrowserNotifyPermission => {
  if (!browserNotifySupported()) return 'unsupported';
  return window.Notification.permission as BrowserNotifyPermission;
};

/** Pede permissão. Só pode ser chamado a partir de um gesto do usuário. */
export const requestBrowserNotifyPermission = async (): Promise<BrowserNotifyPermission> => {
  if (!browserNotifySupported()) return 'unsupported';
  try {
    const result = await window.Notification.requestPermission();
    return result as BrowserNotifyPermission;
  } catch {
    return browserNotifyPermission();
  }
};

export interface ShowBrowserNotificationParams {
  title: string;
  body?: string | null;
  /** Reusado como `tag`: duas notificações com a mesma tag não empilham. */
  tag?: string;
  onClick?: () => void;
}

export const showBrowserNotification = ({
  title, body, tag, onClick,
}: ShowBrowserNotificationParams): boolean => {
  if (browserNotifyPermission() !== 'granted') return false;
  try {
    const notification = new window.Notification(title, {
      body: body || undefined,
      tag,
      icon: '/favicon.png',
    });
    if (onClick) {
      notification.onclick = () => {
        window.focus();
        notification.close();
        onClick();
      };
    }
    return true;
  } catch {
    // Alguns navegadores exigem service worker (Android/Chrome) e estouram
    // no construtor. Silencioso: o sino in-app já mostrou a notificação.
    return false;
  }
};
